// React is imported automatically in newer versions
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchAllData } from '../lib/api';
import { SongCard } from '../components/SongCard';
import { songBelongsToArtist, getArtistNames } from '../utils/artistUtils';

export function Artist() {
  const { id } = useParams();
  const { data, isLoading } = useQuery(['appData'], fetchAllData);

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-64 bg-white/5" />
        <div className="p-8">
          <div className="flex gap-8 mb-12">
            <div className="w-48 h-48 bg-white/10 rounded-xl" />
            <div className="flex-1">
              <div className="h-8 w-48 bg-white/10 rounded mb-4" />
              <div className="h-4 w-96 bg-white/10 rounded mb-2" />
              <div className="h-4 w-72 bg-white/10 rounded" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="aspect-square bg-white/10 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const artist = data.artists.find(a => a.id === id);
  if (!artist) return (
    <div className="p-8 text-center">
      <h2 className="font-outfit text-2xl text-pearl-white mb-4">Artist not found</h2>
      <p className="text-slate-grey mb-6">We couldn't find the artist you're looking for.</p>
      <Link to="/" className="text-naia-pink hover:text-soft-violet transition-colors">
        Return to Home
      </Link>
    </div>
  );

  const artistSongs = data.songs.filter(song => id && songBelongsToArtist(song.artistId, id));

  const getVoicebankName = (synthId: string) => {
    return data.voicebanks.find(v => v.id === synthId)?.name || 'Unknown Voicebank';
  };

  const voicebanks = artist.synthIds
    .map(id => data.voicebanks.find(v => v.id === id))
    .filter(Boolean);

  return (
    <div>
      {/* Banner */}
      {artist.bannerUrl && (
        <div className="relative h-64 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={artist.bannerUrl} 
              alt={`${artist.name} banner`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-deep-night via-deep-night/20 to-transparent" />
          </div>
        </div>
      )}

      <div className="p-8">
        {/* Artist Header */}
        <div className="flex flex-col md:flex-row gap-8 mb-12">
          <div className="w-48 h-48 flex-shrink-0 rounded-xl overflow-hidden ring-1 ring-white/10 shadow-lg">
            {artist.imageUrl ? (
              <img 
                src={artist.imageUrl} 
                alt={artist.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-naia-pink/20 to-deep-night flex items-center justify-center">
                <span className="text-6xl font-outfit font-bold text-naia-pink opacity-50">
                  {artist.name.charAt(0)}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-outfit text-4xl font-bold text-pearl-white mb-4">
              {artist.name}
            </h1>
            <p className="text-slate-grey text-lg mb-6 leading-relaxed">
              {artist.description}
            </p>
            <div className="flex flex-wrap gap-2">
              {voicebanks.map(vb => vb && (
                <span 
                  key={vb.id}
                  className="px-3 py-1.5 bg-white/5 rounded-full text-sm text-slate-grey ring-1 ring-white/10"
                >
                  {vb.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Songs Section */}
        <section>
          <h2 className="font-outfit text-2xl font-semibold mb-6 flex items-center gap-3 relative after:absolute after:bottom-0 after:left-0 after:w-24 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">
            Songs
            <span className="text-slate-grey text-sm font-normal">
              ({artistSongs.length})
            </span>
          </h2>
          {artistSongs.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {artistSongs.map(song => (
                <SongCard
                  key={song.id}
                  song={song}
                  artist={artist.name}
                  voicebank={getVoicebankName(song.synthId)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-grey">
              No songs found for this artist yet.
            </div>
          )}
        </section>
      </div>
    </div>
  );
} 